import { FCElement } from '@fc/component';
import { debounce, onDocumentLoaded } from '@fc/utilities';
import { MegaMenuEnterEvent } from '@fc/events';

const POINTER_ACTIVATE_DELAY = 90;
const DEACTIVATE_DELAY = 80;
const POINTER_IDLE_DELAY = 80;

class HeaderMenu extends FCElement {
  requiredRefs = ['overflowMenu'];
  #ac = null;
  #st = { activeItem: null };
  #skipFocusActivate = false;
  #lastPointer = { x: 0, y: 0 };
  #pointerIdleTimer = null;
  #isTrackingPointer = false;
  #submenuMutationObserver = null;
  #submenuMutationObserverTimer = null;
  #closingItem = null;
  #closeTransitionCleanup = null;

  connectedCallback() {
    super.connectedCallback();

    // Recreate controller on every connect to avoid stacking listeners
    this.#ac?.abort();
    this.#ac = new AbortController();

    // Deactivate when pointer leaves overflow menu
    this.overflowMenu?.addEventListener('pointerleave', () => this.#dbDeactivate(), { signal: this.#ac.signal });
    this.addEventListener('pointerover', this.#handlePointerOver, { signal: this.#ac.signal });
    this.addEventListener('pointerout', this.#handlePointerOut, { signal: this.#ac.signal });
    this.addEventListener('click', this.#handleClick, { capture: true, signal: this.#ac.signal });
    window.addEventListener('resize', this.#handleResize, { signal: this.#ac.signal });

    onDocumentLoaded(this.#preload);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopPointerTracking(this.#st.activeItem);
    this.#cancelCloseTransition();
    this.#cleanupSubmenuHeightRepair();
    this.#setMotionCanvasHeight(0);
    this.#ac?.abort();
    this.#ac = null;
  }

  get animationDelay() {
    const val = parseInt(this.dataset.animationDelay, 10);
    return val >= 0 && isFinite(val) ? val : 0;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get activeSubmenu() {
    return findSubmenu(this.#st.activeItem);
  }

  get activeSubmenuHovered() {
    return this.activeSubmenu?.matches(':hover') ?? false;
  }

  activate = (event) => {
    if (!event) return;
    if (this.#skipFocusActivate && event instanceof FocusEvent) {
      this.#skipFocusActivate = false;
      return;
    }

    this.#dbDeactivate.cancel();
    this.#dbActivate.cancel();
    if (event instanceof FocusEvent) {
      this.#activateHandler(event);
      return;
    }

    this.#dbActivate(event);
  };

  #activateHandler = (event) => {
    if (!event?.target) return;
    this.#dbDeactivate.cancel();
    this.#cancelCloseTransition();

    this.dispatchEvent(new MegaMenuEnterEvent());

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#st.activeItem) return;

    this.#activateItem(item);
  };

  #activateItem = (item) => {
    const isDefaultSlot = !isOverflowItem(item);
    let submenu = findSubmenu(item);

    if (!submenu && isDefaultSlot) {
      this.#closeItem(this.#st.activeItem);
      return;
    }

    this.dataset.overflowExpanded = String(!isDefaultSlot);

    // Deactivate previously active item
    const previousItem = this.#st.activeItem;
    if (previousItem) {
      this.#st.activeItem.ariaExpanded = 'false';
    }

    // Set new active item
    this.#st.activeItem = item;
    this.ariaExpanded = 'true';
    if (item.getAttribute('aria-haspopup') === 'true' || !isDefaultSlot) {
      item.ariaExpanded = 'true';
    }
    this.#setMoreExpanded(!isDefaultSlot);
    this.#syncOverflowAnchor(!isDefaultSlot);

    // Determine submenu and calculate height
    const overflowH = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    this.#prepareActiveSubmenu(submenu, previousItem);
    this.#syncSubmenuHeight(submenu, overflowH);
    this.#startPointerTracking(item, previousItem);
  };

  #dbActivate = debounce(this.#activateHandler, POINTER_ACTIVATE_DELAY);

  #handleClick = (event) => {
    if (!(event instanceof MouseEvent) || !(event.target instanceof Element)) return;

    const link = event.target.closest('.menu-list__link[data-ref="menuitem"]');
    if (!(link instanceof HTMLElement) || link.closest('.menu-list__submenu')) return;
    if (!this.contains(link)) return;

    const linkBounds = link.getBoundingClientRect();
    if (event.clientY > linkBounds.bottom + 1) {
      event.preventDefault();
      event.stopPropagation();
      this.#closeItem(this.#st.activeItem);
      return;
    }

    if (this.#st.activeItem && this.#st.activeItem !== link) {
      this.#closeItem(this.#st.activeItem);
    }
  };

  #handleDocumentPointerMove = (event) => {
    const item = this.#st.activeItem;
    if (!item) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || Math.abs(event.movementY) >= 1;
    this.#setSafetyBox(item, moving);

    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = setTimeout(() => {
      this.#setSafetyBox(this.#st.activeItem, false);
      this.#reconcilePointerTarget();
    }, POINTER_IDLE_DELAY);
  };

  #startPointerTracking(item, previousItem) {
    if (previousItem && previousItem !== item) {
      previousItem.style.removeProperty('--fit-menu-safety-box-height');
      this.#setSafetyBox(previousItem, false);
    }

    const header = this.closest('#fit-site-header') ?? this.closest('.header') ?? this;
    const itemBounds = item.getBoundingClientRect();
    const headerBounds = header.getBoundingClientRect();
    item.style.setProperty('--fit-menu-safety-box-height', `${Math.max(itemBounds.height, headerBounds.bottom - itemBounds.top)}px`);

    if (this.#isTrackingPointer) return;

    document.body.addEventListener('pointermove', this.#handleDocumentPointerMove, { passive: true });
    this.#isTrackingPointer = true;
  }

  #stopPointerTracking(item = this.#st.activeItem) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = null;

    if (item) {
      item.style.removeProperty('--fit-menu-safety-box-height');
      this.#setSafetyBox(item, false);
    }

    if (!this.#isTrackingPointer) return;

    document.body.removeEventListener('pointermove', this.#handleDocumentPointerMove);
    this.#isTrackingPointer = false;
  }

  #setSafetyBox(item, enabled) {
    if (!item) return;

    if (enabled) {
      item.dataset.fitSafetyBox = 'true';
    } else {
      delete item.dataset.fitSafetyBox;
    }
  }

  #reconcilePointerTarget() {
    const target = document.elementFromPoint(this.#lastPointer.x, this.#lastPointer.y);
    if (!(target instanceof Element)) return;

    const submenu = this.activeSubmenu;
    if (submenu?.contains(target)) return;

    const listItem = target.closest('.menu-list__list-item');
    if (!(listItem instanceof Element) || !this.contains(listItem)) {
      this.#deactivate();
      return;
    }

    const item = findMenuItem(listItem);
    if (!item || item === this.#st.activeItem) return;

    this.#dbDeactivate.cancel();
    this.#activateItem(item);
  }

  #handlePointerOver = (event) => {
    if (!(event.target instanceof Element)) return;
    const submenu = this.activeSubmenu;
    if (!submenu || !submenu.contains(event.target)) return;
    this.#dbDeactivate.cancel();
  };

  #handlePointerOut = (event) => {
    if (!(event.target instanceof Element)) return;
    const submenu = this.activeSubmenu;
    if (!submenu || !submenu.contains(event.target)) return;

    const next = event.relatedTarget;
    if (next instanceof Element && submenu.contains(next)) return;

    this.#dbDeactivate();
  };

  #handleResize = debounce(() => {
    if (this.dataset.overflowExpanded === 'true') {
      this.#syncOverflowAnchor(true);
    }
  }, POINTER_IDLE_DELAY);

  handleKeyDown = (event) => {
    if (!(event instanceof KeyboardEvent)) return;

    const item = findMenuItem(event.target);
    const moreTrigger = getMoreTrigger(event.target);
    if (!(item instanceof HTMLElement) || (!moreTrigger && item.getAttribute('aria-haspopup') !== 'true')) return;
    const origin = event.composedPath?.()[0];
    const originElement = origin instanceof Element ? origin : null;
    const trigger = moreTrigger ?? item;

    if (originElement && !trigger.contains(originElement) && event.key !== 'Escape') return;

    const expanded = item.getAttribute('aria-expanded') === 'true';

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      if (!moreTrigger) return;

      event.preventDefault();
      this.activate(event);
      trigger.focus();
      return;
    }

    if (event.key !== 'Escape' || !expanded) return;

    event.preventDefault();
    this.#st.activeItem = item;
    this.#closeItem(item);
    this.#skipFocusActivate = true;
    trigger.focus();
  };

  deactivate(event) {
    if (!event?.target) return;
    this.#dbActivate.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (item && item === this.#st.activeItem) {
      this.#dbDeactivate();
    }
  }

  #closeItem = (item) => {
    if (!item) return;

    this.#stopPointerTracking(item);
    this.#clearActiveSubmenu(item);
    this.#cleanupSubmenuHeightRepair();

    // Reset submenu
    this.style.setProperty('--submenu-height', '0px');
    this.#setMotionCanvasHeight(0);
    this.dataset.overflowExpanded = 'false';
    this.#setMoreExpanded(false);
    this.#syncOverflowAnchor(false);

    // Clear active state
    this.#st.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Trigger closing animation
    item.setAttribute('data-animating', '');
    this.#holdOpenSurfaceThroughClose(item);
  };

  #holdOpenSurfaceThroughClose(item) {
    this.#cancelCloseTransition();
    this.#closingItem = item;

    const surface = isOverflowItem(item) ? this.overflowMenu : findSubmenu(item);
    let timer = null;

    const finish = () => {
      if (this.#closingItem !== item) return;
      item.removeAttribute('data-animating');
      this.#closingItem = null;
      this.#closeTransitionCleanup?.();
    };

    const onTransitionEnd = (event) => {
      if (event.target === surface && event.propertyName === 'clip-path') finish();
    };

    if (surface instanceof HTMLElement) {
      surface.addEventListener('transitionend', onTransitionEnd);
    }

    timer = setTimeout(finish, this.animationDelay + 100);
    this.#closeTransitionCleanup = () => {
      if (surface instanceof HTMLElement) {
        surface.removeEventListener('transitionend', onTransitionEnd);
      }
      clearTimeout(timer);
      this.#closeTransitionCleanup = null;
    };
  }

  #cancelCloseTransition() {
    this.#closeTransitionCleanup?.();
    this.#closingItem?.removeAttribute('data-animating');
    this.#closingItem = null;
  }

  #prepareActiveSubmenu(submenu, previousItem) {
    const previousSubmenu = findSubmenu(previousItem);
    if (previousSubmenu instanceof HTMLElement && previousSubmenu !== submenu) {
      delete previousSubmenu.dataset.active;
    }

    const overflowMenu = this.overflowMenu;
    if (overflowMenu instanceof HTMLElement && overflowMenu !== submenu) {
      delete overflowMenu.dataset.active;
    }

    this.#cleanupSubmenuHeightRepair();

    if (!(submenu instanceof HTMLElement)) return;

    submenu.dataset.active = '';
    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.#st.activeItem && submenu.hasAttribute('data-active') && submenu.offsetHeight > 0) {
            this.#syncSubmenuHeight(submenu);
            this.#cleanupSubmenuHeightRepair();
          }
        });
      });
    });
    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
    this.#submenuMutationObserverTimer = setTimeout(() => this.#cleanupSubmenuHeightRepair(), 500);
  }

  #clearActiveSubmenu(item) {
    const submenu = findSubmenu(item);
    if (submenu instanceof HTMLElement) {
      delete submenu.dataset.active;
    }

    const overflowMenu = this.overflowMenu;
    if (overflowMenu instanceof HTMLElement) {
      delete overflowMenu.dataset.active;
    }
  }

  #cleanupSubmenuHeightRepair() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;

    if (this.#submenuMutationObserverTimer !== null) {
      clearTimeout(this.#submenuMutationObserverTimer);
      this.#submenuMutationObserverTimer = null;
    }
  }

  #syncSubmenuHeight(submenu, overflowHeight = this.overflowMenu?.offsetHeight ?? 0) {
    const submenuHeight = submenu instanceof HTMLElement ? Math.max(submenu.offsetHeight, overflowHeight) : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.#setMotionCanvasHeight(submenuHeight);
  }

  #setMotionCanvasHeight(submenuHeight) {
    const header = this.closest('#fit-site-header');
    if (!(header instanceof HTMLElement)) return;

    const headerHeight = header.getBoundingClientRect().height;
    header.style.setProperty('--fit-header-motion-rest-height', `${Math.ceil(headerHeight)}px`);

    if (submenuHeight <= 0) {
      header.style.setProperty('--fit-header-motion-height', `${Math.ceil(headerHeight)}px`);
      return;
    }

    header.style.setProperty('--fit-header-motion-height', `${Math.ceil(headerHeight + submenuHeight)}px`);
  }

  #deactivate = (item = this.#st.activeItem) => {
    if (!item || item !== this.#st.activeItem) return;
    if (item.dataset.fitSafetyBox === 'true') return;
    if (this.overflowHovered || this.activeSubmenuHovered) return;
    this.#closeItem(item);
  };

  #dbDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #setMoreExpanded(expanded) {
    const control = this.querySelector('.menu-list__list-item[slot="more"] .menu-list__link[aria-expanded]');
    if (control instanceof HTMLElement) control.ariaExpanded = String(expanded);
  }

  #syncOverflowAnchor(expanded) {
    const overflowMenu = this.refs.overflowMenu;
    if (!(overflowMenu instanceof HTMLElement)) return;

    if (!expanded) {
      overflowMenu.style.removeProperty('--fit-overflow-menu-anchor-inline-start');
      overflowMenu.style.removeProperty('--fit-overflow-menu-anchor-inline-size');
      overflowMenu.style.removeProperty('--fit-overflow-menu-measured-column-inline-size');
      return;
    }

    const control = this.querySelector('.menu-list__list-item[slot="more"] .menu-list__link');
    const surface = overflowMenu.shadowRoot?.querySelector('[part="overflow"]');
    if (!(control instanceof HTMLElement)) return;

    const controlRect = control.getBoundingClientRect();
    const surfaceRect = surface instanceof HTMLElement ? surface.getBoundingClientRect() : overflowMenu.getBoundingClientRect();
    if (!isFinite(surfaceRect.width) || surfaceRect.width <= 0) return;

    const direction = getComputedStyle(overflowMenu).direction;
    const inlineStart =
      direction === 'rtl' ? surfaceRect.right - controlRect.right : controlRect.left - surfaceRect.left;
    const columnWidth = Array.from(this.querySelectorAll('.menu-list__list-item[slot="overflow"] > .menu-list__link')).reduce(
      (max, link) => {
        if (!(link instanceof HTMLElement)) return max;
        const linkRect = link.getBoundingClientRect();
        return Math.max(max, linkRect.width, link.scrollWidth);
      },
      controlRect.width
    );

    overflowMenu.style.setProperty('--fit-overflow-menu-anchor-inline-start', `${Math.max(0, inlineStart).toFixed(2)}px`);
    overflowMenu.style.setProperty('--fit-overflow-menu-anchor-inline-size', `${Math.max(0, controlRect.width).toFixed(2)}px`);
    overflowMenu.style.setProperty('--fit-overflow-menu-measured-column-inline-size', `${Math.max(0, columnWidth).toFixed(2)}px`);
  }

  #preload = () => {
    for (const img of this.querySelectorAll('img[loading="lazy"]')) {
      img.removeAttribute('loading');
    }
  };
}

if (!customElements.get('nav-menu')) {
  customElements.define('nav-menu', HeaderMenu);
}

// Finds the closest menu item element
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // Special handling for "More" slot — select first overflow sibling
  const moreTrigger = element.closest('[slot="more"]');
  if (moreTrigger) {
    const overflow = moreTrigger.parentElement?.querySelector('[slot="overflow"]');
    if (overflow) return findMenuItem(overflow);
  }

  const menuItem = element.closest('.menu-list__list-item');
  return menuItem?.querySelector('[data-ref="menuitem"]') ?? element.querySelector('[data-ref="menuitem"]');
}

// Finds the submenu for a given menu item element
function findSubmenu(element) {
  return element?.parentElement?.querySelector('[data-ref="submenu[]"]') ?? null;
}

function isOverflowItem(element) {
  return element?.parentElement?.slot === 'overflow' || element?.closest('[slot="overflow"]') != null;
}

function getMoreTrigger(element) {
  if (!(element instanceof Element)) return null;

  const more = element.closest('[slot="more"]');
  const trigger = more?.querySelector('.menu-list__link');
  return trigger instanceof HTMLElement ? trigger : null;
}
